<template>
  <!-- Base z-index is set to 10001 to ensure the modal appears above the -->
  <!-- floating-vue dropdown. -->
  <modal-wrapper
    ref="modalRef"
    :base-z-index="10001"
    :header="$t(`remarksModal.title.${mode}`)"
    @ok-button-click="handleOk"
  >
    <textarea
      v-model="remarks"
      :placeholder="$t(`remarksModal.remarksPlaceHolder.${mode}`)"
    ></textarea>
    <p v-if="isNoRemarksError">
      {{ $t(`remarksModal.errors.enterRemarks.${mode}`) }}
    </p>
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { InspectorStatusEnum } from 'types';
  import {
    IRemarksModalEventPayload,
    RemarksModalMode,
  } from './RemarksModal.types';
  import { useModalWrapper } from '../ModalWrapper';

  const props = defineProps<{ mode?: RemarksModalMode }>();

  const emit = defineEmits<{
    (e: 'remarks-submitted', payload: IRemarksModalEventPayload): void;
  }>();

  const remarks = ref('');
  const isNoRemarksError = ref(false);

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  function handleOk() {
    isNoRemarksError.value = false;

    if (!remarks.value) {
      isNoRemarksError.value = true;
      createErrorToast(t(`remarksModal.errors.enterRemarks.${props.mode}`));
      return;
    }

    const modeToStatus: { [mode in RemarksModalMode]: InspectorStatusEnum } = {
      [RemarksModalMode.ACCEPT_WITH_REMARKS]:
        InspectorStatusEnum.acceptedWithRemarks,
      [RemarksModalMode.REJECT]: InspectorStatusEnum.rejected,
    };

    const eventPayload: IRemarksModalEventPayload = {
      remarks: remarks.value,
      status: modeToStatus[props.mode!],
    };

    emit('remarks-submitted', eventPayload);
    hide();
  }
</script>

<style lang="scss" scoped>
  :deep(.p-dialog-mask) {
    z-index: 10001 !important;
  }

  textarea {
    width: 100%;
    padding: 10px;
  }

  p {
    font-size: 16px;
    color: red;
    text-align: center;
  }
</style>
